package fyi.moko.hotpl.data.service

import fyi.moko.hotpl.domain.model.Emotion
import fyi.moko.hotpl.domain.model.normalizeEmotionLabel
import fyi.moko.hotpl.domain.service.EmotionLabelPreference
import kotlinx.browser.localStorage
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/**
 * `localStorage`-backed [EmotionLabelPreference]; one key per emotion id. Defensive the same
 * way [JsHomeViewPreference] is — a name that fails to persist is not worth taking the screen down for.
 */
class JsEmotionLabelPreference : EmotionLabelPreference {
    private val _labels = MutableStateFlow(load())
    override val labels = _labels.asStateFlow()

    override suspend fun setLabel(emotionId: String, label: String?) {
        val normalized = normalizeEmotionLabel(label)
        _labels.update { if (normalized == null) it - emotionId else it + (emotionId to normalized) }
        try {
            if (normalized == null) {
                localStorage.removeItem(key(emotionId))
            } else {
                localStorage.setItem(key(emotionId), normalized)
            }
        } catch (e: Throwable) {
            console.error("Failed to persist the emotion label to localStorage", e)
        }
    }

    private fun load(): Map<String, String> = buildMap {
        Emotion.entries.forEach { emotion ->
            val stored = try {
                localStorage.getItem(key(emotion.id))
            } catch (e: Throwable) {
                console.error("Failed to read the emotion label from localStorage", e)
                null
            }
            normalizeEmotionLabel(stored)?.let { put(emotion.id, it) }
        }
    }

    private fun key(emotionId: String) = KEY_PREFIX + emotionId

    private companion object {
        const val KEY_PREFIX = "hotpl_emotion_label_"
    }
}
